import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { useSeo } from "@/lib/useSeo";

/**
 * Shared layout for static legal / informational pages.
 *
 * Props:
 *  - title (string)         — H1 shown on the page (also used for <title>)
 *  - metaTitle (string)     — <title>
 *  - description (string)   — <meta description>
 *  - path (string)          — canonical path e.g. "/o-nas"
 *  - children               — body content (H2 sections)
 */
const LegalLayout = ({ title, metaTitle, description, path, children }) => {
  const location = useLocation();
  useSeo({ title: metaTitle, description, path });

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="App font-body text-[#111111] bg-white">
      <Header />
      <main className="pt-28 md:pt-36 pb-20 md:pb-28">
        <div className="mx-auto max-w-[920px] px-6 md:px-12">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F5C69] hover:gap-3 transition-all mb-10"
          >
            <ArrowLeft size={16} strokeWidth={2.5} />
            Zpět na úvod
          </Link>

          <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69] mb-4">
            SANVIORO
          </div>

          <h1
            data-testid="legal-h1"
            className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] text-[#111111]"
          >
            {title}
          </h1>

          <div
            className="mt-12 md:mt-16 space-y-12 md:space-y-16 text-[17px] md:text-[18px] leading-[1.75] text-[#333333]"
            data-testid="legal-content"
          >
            {children}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const Section = ({ title, children }) => (
  <section className="border-t border-[#E5E5E5] pt-10 md:pt-12">
    <h2 className="font-display text-2xl md:text-4xl font-bold tracking-[-0.02em] leading-[1.05] text-[#111111] mb-6 md:mb-8">
      {title}
    </h2>
    <div className="space-y-4">{children}</div>
  </section>
);

export const InlineLink = ({ to, children, external }) =>
  external ? (
    <a
      href={to}
      className="text-[#0F5C69] underline decoration-[#0F5C69]/40 underline-offset-4 hover:decoration-[#0F5C69] transition-colors"
    >
      {children}
    </a>
  ) : (
    <Link
      to={to}
      className="text-[#0F5C69] underline decoration-[#0F5C69]/40 underline-offset-4 hover:decoration-[#0F5C69] transition-colors"
    >
      {children}
    </Link>
  );

export default LegalLayout;
